import React, { Component } from "react";
import moment from "moment";

import {
  CustomizedXAxisTickTimeDay,
  CustomizedYAxisTick,
  StandardTooltip,
  selectFormat,
  formatDateFromData,
  isDateFromDataInISOFormat,
  Error,
} from "./../Utils/Utils.js";
import { XRayTooltip } from "./../Utils/XRayTooltip/XRayTooltip.js";

import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  ReferenceDot,
} from "recharts";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  InsightsLineLabel,
  InsightsTickIndicator,
  closeAllLinechartTooltips,
} from "./../Utils/InsightsUtils.js";

import { getXrayTooltipPosition } from "helpers/DOM";

const renderDateLabel = (data) => {
  let label = "";
  const numDays = getNumberOfDays(data);
  if (numDays > 360) {
    label = "This Month";
  } else if (numDays > 90) {
    label = "This Week";
  } else {
    label = "This Day";
  }

  return label;
};

const getNumberOfDays = (tooltip_data) => {
  const previous_date = moment(tooltip_data.previous_date);
  const current_date = moment(tooltip_data.current_date);

  const days = current_date.diff(previous_date, "days");
  return days;
};

const formatText = (tooltip_data) => {
  const diff = getNumberOfDays(tooltip_data);

  if (diff > 360) {
    return diff
      ? Math.floor(diff / 30) +
          " month" +
          (Math.floor(diff / 30) > 1 ? "s" : "") +
          " prior"
      : "No months prior";
  } else if (diff > 90) {
    return diff
      ? Math.floor(diff / 7) +
          " week" +
          (Math.floor(diff / 7) > 1 ? "s" : "") +
          " prior"
      : "No weeks prior";
  } else {
    return diff
      ? diff + " day" + (diff > 1 ? "s" : "") + " prior"
      : "No days prior";
  }
};

class FreeTrialStarts extends Component {
  constructor(props) {
    super(props);
    this.node = React.createRef();
    this.state = {
      widgetsData: props.data.data,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    closeAllLinechartTooltips();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  getExportData = () => ({
    data: this.props.data.data.map((d) => {
      d["current_label"] = renderDateLabel(d);
      return d;
    }),
    opts: {
      type: "chart",
      values: ["current", "previous"],
      date: {
        // overwrites the default settings for date only when the date is in ISO format
        defined: isDateFromDataInISOFormat(this.props.data.data),
        format: "YYYY-MM-DDThh:mm:ssZ",
        viewFormat: "YYYY-MM-DD",
      },
    },
  });

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    if (
      this.divPosition.current &&
      this.divPosition.current.contains(e.target)
    ) {
      return;
    } else {
      closeAllLinechartTooltips();
    }
  };

  render() {
    const { data, title } = this.props;
    const { widgetsInsights } = this.state;

    const widget_data = data.data;

    const insightsData = widgetsInsights.length > 0;

    if (data.hasData === false) {
      return <Error minHeight={400} />;
    }

    let preparedData = formatDateFromData(widget_data);

    return (
      <div className="row" ref={this.divPosition}>
        <ResponsiveContainer height={400} width="100%">
          <ComposedChart
            data={preparedData}
            margin={{ top: 12, right: 0, bottom: 15, left: 0 }}
            cursor="pointer"
            onMouseOver={(e) => CloseSelectedInsight(this.node)}
            onMouseOut={(e) =>
              OpenSelectedInsight(this.node, !this.state.tooltip.show)
            }
            isAnimationActive={false}
            onClick={(d) => {
              if (d !== null) {
                const extra = {
                  date: d.activePayload[0].payload.date,
                  current: d.activePayload[0].payload.current,
                  current_date: d.activePayload[0].payload.current_date,
                  current_value: d.activePayload[0].payload.current_value,

                  previous: d.activePayload[0].payload.previous,
                  previous_date: d.activePayload[0].payload.previous_date,
                  previous_value: d.activePayload[0].payload.previous_value,
                };
                d.extra = extra;

                const indicator = "freeTrialStarts";
                d.indicator = indicator;

                const renderLabel = renderDateLabel(extra);
                d.renderLabel = renderLabel;
                const formattedText = formatText(extra);
                d.formattedText = formattedText;

                this.setState({
                  tooltip: { ...d, show: true },
                });
              } else {
                this.closeTooltip();
              }
            }}
          >
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              scale="auto"
              tick={<CustomizedXAxisTickTimeDay />}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              mirror={true}
              tick={<CustomizedYAxisTick />}
            />

            <CartesianGrid
              stroke="#FEE5DE"
              strokeDasharray="3 3"
              vertical={false}
            />

            <Line
              type="monotone"
              dataKey="current"
              stroke="#fc6d52"
              strokeWidth="2"
              dot={false}
            />

            <Area
              type="monotone"
              dataKey="previous"
              fillOpacity={0.2}
              fill="#f89481"
              stroke="none"
              isAnimationActive={false}
            >
              {this.state.widgetsInsights.length > 0 && (
                <LabelList
                  dataKey="date"
                  content={
                    <InsightsLineLabel
                      height={325}
                      data={preparedData}
                      insights={this.state.widgetsInsights}
                      showIndicator={!this.state.tooltip.show}
                      node={this.node}
                    />
                  }
                />
              )}
            </Area>

            {!this.state.tooltip.show && (
              <Tooltip
                cursor={{ fill: "#eee", opacity: 0.5 }}
                content={
                  <StandardTooltip
                    render={({ data }) => {
                      const payload = data(data);
                      if (payload !== null) {
                        const tooltip_data = payload[0].payload;

                        return (
                          <div className="standard-tooltip">
                            <header className="standard-tooltip__header">
                              <span className="standard-tooltip__key-header">
                                Date:
                              </span>
                              <span className="standard-tooltip__value-header">
                                {moment(tooltip_data.current_date).format(
                                  "ddd, MMMM DD"
                                )}
                              </span>
                            </header>

                            <main className="standard-tooltip__main">
                              <div className="standard-tooltip__item">
                                <span className="standard-tooltip__key">
                                  <span
                                    className="standard-tooltip__icon"
                                    style={{ background: "#fc6d52" }}
                                  />
                                  {renderDateLabel(tooltip_data)}
                                </span>
                                <span className="standard-tooltip__value">
                                  {selectFormat(
                                    "number",
                                    tooltip_data.current_value
                                  )}
                                </span>
                              </div>

                              <div className="standard-tooltip__item">
                                <span className="standard-tooltip__key">
                                  <span
                                    className="standard-tooltip__icon"
                                    style={{
                                      background: "#fc6d52",
                                      opacity: 0.2,
                                    }}
                                  />
                                  {formatText(tooltip_data)}
                                </span>
                                <span className="standard-tooltip__value">
                                  {selectFormat(
                                    "number",
                                    tooltip_data.previous_value
                                  )}
                                </span>
                              </div>

                              {widgetsInsights.length > 0 && (
                                <InsightsWithinTooltip
                                  data={widgetsInsights}
                                  tooltipDate={tooltip_data.date}
                                />
                              )}
                            </main>
                          </div>
                        );
                      }
                    }}
                  />
                }
              />
            )}
            {/* Adds insight icon under x-axis ticks  */}
            {insightsData &&
              widgetsInsights.map((o, index) => (
                <ReferenceDot
                  x={o.date}
                  y={0}
                  key={title + index}
                  ifOverflow="extendDomain"
                  shape={
                    <InsightsTickIndicator
                      insights={widgetsInsights}
                      date={o.date}
                      node={this.node}
                    />
                  }
                />
              ))}
          </ComposedChart>
        </ResponsiveContainer>
        {this.state.tooltip.activeCoordinate ? (
          <div
            className="xray-tooltip-container"
            style={{
              top: this.state.tooltip.activeCoordinate.y + "px",
              left:
                this.state.tooltip.activeCoordinate.x + 1062 + 220 <
                this.state.width
                  ? this.state.tooltip.activeCoordinate.x + 15 + "px"
                  : this.state.tooltip.activeCoordinate.x -
                    getXrayTooltipPosition(
                      this.divPosition.current,
                      this.state.tooltip.activeCoordinate
                    ) +
                    "px",
              opacity: this.state.tooltip.show ? 1.0 : 0.0,
              visibility: this.state.tooltip.show ? "visible" : "hidden",
            }}
          >
            <XRayTooltip
              data={this.state.tooltip}
              insights={widgetsInsights}
              close={this.closeTooltip.bind(this)}
            />
          </div>
        ) : null}

        <div className="featured-insight-tooltip" ref={this.node}>
          {widgetsInsights.length > 0 && !this.state.tooltip.show && (
            <RenderTooltipInsights data={widgetsInsights} />
          )}
        </div>
      </div>
    );
  }
}

export default FreeTrialStarts;
